import React, { useState, useEffect, FormEvent, ChangeEvent } from 'react';
import { Container, Row, Col, Form, Button, Alert, Image } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../../../api/axios';
import Header from '../layout/Header';
import './RespondenseForm.css';
import { getClientBrowserInfo, getClientOSInfo, getClientIPAddress, containsEmoji, formatSurveyResponse } from '../../../utils/Utils';
import { 
  RESPONSE_FORM_STEP, 
  RELATIONSHIP_OPTIONS, 
  getRelationshipString,
  PERIOD_OPTIONS,
  getPeriodString,
  ACTIVITY_FREQUENCY_OPTIONS,
  getActivityFrequencyString
} from '../../../utils/Constants';
import { ApiError } from '../../../types/errors';
import { QuestionItem, Survey, SocialSkill } from '../../../types';

interface CandidateInfo {
  id: number;
  name: string;
  company: string;
  position: string;
}

interface RespondentInfo {
  id: number;
  candidate_id: number;
  name: string;
  relationship: number;
  email: string;
}

interface FormData {
  relationship: {  // 関係性
    private: string;           // 仕事かプライベートか
    detail: number;            // 関係性の詳細（上司、部下、同僚など）
    workCompany: string;       // 会社・団体名
    workDetails: string;       // 活動内容
    workPeriodStart: { year: number; season: number; };
    workPeriodEnd: { year: number; season: number; };
    currentlyWorking: boolean; // 現在も働いているか
    activityFrequency: number; // 活動頻度
  };

  descriptiveQuestions: { // 選択された質問テンプレートの内容
    [key: string]: string;
  };

  socialSkills: {  // 選択式質問
    [key: string]: number | null;
  };

  questionnaire: {  // アンケート
    [key: string]: string | undefined;
  };
}

interface FormErrors {
  [key: string]: string | undefined;
  submit?: string;
}

const RespondenseForm: React.FC = () => {
  const { token } = useParams<{ token?: string }>();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState<number>(RESPONSE_FORM_STEP.RELATIONSHIP);
  const [loading, setLoading] = useState<boolean>(false);
  const [candidateInfo, setCandidateInfo] = useState<CandidateInfo | null>(null);
  const [respondentInfo, setRespondentInfo] = useState<RespondentInfo | null>(null);
  const [formData, setFormData] = useState<FormData>({
    relationship: {
      private: '',
      detail: 0,
      workCompany: '',
      workDetails: '',
      workPeriodStart: { year: -1, season: 0 },
      workPeriodEnd: { year: -1, season: 0 },
      currentlyWorking: false,
      activityFrequency: 0
    },
    descriptiveQuestions: {},
    socialSkills: {},
    questionnaire: {}
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isComplete, setIsComplete] = useState<boolean>(false);
  const [isConflict, setIsConflict] = useState<boolean>(false);
  const [dynamicQuestions, setDynamicQuestions] = useState<Array<QuestionItem>>([]);
  const [surveyQuestions, setSurveyQuestions] = useState<Array<Survey>>([]);
  const [socialSkillQuestions, setSocialSkillQuestions] = useState<Array<SocialSkill>>([]);

  useEffect(() => {
    const fetchCandidateInfo = async (): Promise<void> => {
      try {
        setLoading(true);

        const response = await axios.get(`/respondent/verify-token/${token}`);
        setCandidateInfo(response.data.candidate);
        setRespondentInfo(response.data.respondent);

        if (response.data.candidate.question_template && response.data.candidate.question_template.questions) {
          const questions = response.data.candidate.question_template.questions || [];
          const sortedQuestions = questions.sort((a: QuestionItem, b: QuestionItem) => a.order - b.order);
          setDynamicQuestions(sortedQuestions);
        }

        if (response.data.surveys) {
          const surveyData = response.data.surveys;
          if (surveyData) {
            const sortedSurveys = surveyData.sort((a: Survey, b: Survey) => a.order - b.order);
            setSurveyQuestions(sortedSurveys);
          }
        }

        if (response.data.socialSkills) {
          const socialSkillData = response.data.socialSkills;
          if (socialSkillData) {
            const sortedSocialSkills = socialSkillData.sort((a: SocialSkill, b: SocialSkill) => a.order - b.order);
            setSocialSkillQuestions(sortedSocialSkills);
          }
        }

        if (!response.data.respondent.is_verified) {
          // 回答者の本人確認ができなかった場合、回答者情報入力画面に遷移する。
          navigate(`/respondent/verify/${token}`);
        }

        setLoading(false);
      } catch (error: unknown) {
        console.error('Error fetching candidate info:', error);

        const axiosError = error as ApiError;

        if (axiosError.response && axiosError.response.status === 404) {
          navigate('/404');
          return;
        }

        if (axiosError.response && axiosError.response.status == 409) {
          // 既に回答済みの場合、完了画面を表示する。
          setIsConflict(true);
          setLoading(false);
          return;
        }

        if (axiosError.response && axiosError.response.status == 429) {
          // 回答上限を超えた場合、完了画面を表示する。
          setIsConflict(true);
          setLoading(false);
          return;
        }

        setLoading(false);
      }
    };

    if (token) {
      fetchCandidateInfo();
    } else {
      navigate('/404');
    }
  }, [token, navigate]);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void => {
    const { name, value } = e.target;

    if (name.includes('.')) {
      const [parent, ...rest] = name.split('.');
      const child = rest.join('.');

      if ((parent === 'relationship' && (child === 'workPeriodStart.year' || child === 'workPeriodEnd.year')) ||
          (child === 'year')) {
        const numValue = value === '' ? -1 : parseInt(value, 10);

        if (child.includes('.')) {
          const [subParent, subChild] = child.split('.');
          setFormData(prev => {
            const updatedParent = {
              ...prev[parent as keyof FormData],
              [subParent]: {
                ...(prev[parent as keyof FormData] as any)[subParent],
                [subChild]: numValue
              }
            };

            return {
              ...prev,
              [parent]: updatedParent
            };
          });
        } else {
          setFormData(prev => {
            const updatedParent = {
              ...prev[parent as keyof FormData],
              [child]: numValue
            };

            return {
              ...prev,
              [parent]: updatedParent
            };
          });
        }
      }
      else if ((parent === 'relationship' && (child === 'workPeriodStart.season' || child === 'workPeriodEnd.season')) ||
              (child.includes('.') && child.split('.')[1] === 'season')) {
        if (child.includes('.')) {
          const [subParent, subChild] = child.split('.');
          setFormData(prev => {
            const updatedParent = {
              ...prev[parent as keyof FormData],
              [subParent]: {
                ...(prev[parent as keyof FormData] as any)[subParent],
                [subChild]: value
              }
            };

            return {
              ...prev,
              [parent]: updatedParent
            };
          });
        } else {
          setFormData(prev => {
            const updatedParent = {
              ...prev[parent as keyof FormData],
              [child]: value
            };

            return {
              ...prev,
              [parent]: updatedParent
            };
          });
        }
      }
      else if (parent === 'socialSkills') {
        const intValue = parseInt(value);
        setFormData(prev => {
          const updatedSocialSkills = {
            ...prev.socialSkills,
            [child]: intValue
          };

          return {
            ...prev,
            socialSkills: updatedSocialSkills
          };
        });
      }
      else if (parent === 'questionnaire') {
        setFormData(prev => {
          const updatedQuestionnaire = {
            ...prev.questionnaire,
            [child]: value
          };

          return {
            ...prev,
            questionnaire: updatedQuestionnaire
          };
        });
      }
      else {
        setFormData(prev => {
          const updatedParent = {
            ...prev[parent as keyof FormData],
            [child]: value
          };

          return {
            ...prev,
            [parent]: updatedParent
          };
        });
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const { name, checked } = e.target;

    if (name.includes('.')) {
      const [parent, child] = name.split('.');

      if (parent === 'relationship' && child === 'currentlyWorking') {
        setFormData(prev => ({
          ...prev,
          relationship: {
            ...prev.relationship,
            currentlyWorking: checked,
            ...(checked ? { workPeriodEnd: { year: -1, season: 0 } } : {})
          }
        }));
      } else {
        setFormData(prev => {
          const newData = { ...prev };
          (newData[parent as keyof FormData] as any)[child] = checked;
          return newData;
        });
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: checked }));
    }
  };

  const validateStep = (step: number): FormErrors => {
    const newErrors: FormErrors = {};

    if (step === RESPONSE_FORM_STEP.RELATIONSHIP) {
      if (!formData.relationship.private) newErrors['relationship.private'] = '関係性を選択してください';
      if (!formData.relationship.detail) newErrors['relationship.detail'] = '関係性の詳細を選択してください';
      if (!formData.relationship.workCompany) newErrors['relationship.workCompany'] = '会社・団体名を入力してください。';
      else if (containsEmoji(formData.relationship.workCompany)) newErrors['relationship.workCompany'] = '絵文字は使用できません。';

      if (!formData.relationship.workDetails.trim()) newErrors['relationship.workDetails'] = '業務内容を入力してください。';
      else if (containsEmoji(formData.relationship.workDetails)) newErrors['relationship.workDetails'] = '絵文字は使用できません。';

      if (!formData.relationship.activityFrequency) newErrors['relationship.activityFrequency'] = '活動頻度を選択してください';

      if (formData.relationship.workPeriodStart.year === -1) newErrors['relationship.workPeriodStart.year'] = '開始年を選択してください';
      if (!formData.relationship.workPeriodStart.season) newErrors['relationship.workPeriodStart.season'] = '開始時期を選択してください';

      if (!formData.relationship.currentlyWorking) {
        if (formData.relationship.workPeriodEnd.year === -1) {
          newErrors['relationship.workPeriodEnd.year'] = '終了年を選択してください';
        } else if (formData.relationship.workPeriodStart.year > formData.relationship.workPeriodEnd.year) {
          newErrors['relationship.workPeriodEnd.year'] = '終了年を正しく選択してください';
        }
        if (!formData.relationship.workPeriodEnd.season) {
          newErrors['relationship.workPeriodEnd.season'] = '終了時期を選択してください';
        } else if (formData.relationship.workPeriodStart.year == formData.relationship.workPeriodEnd.year &&
            (formData.relationship.workPeriodStart.season != 4 && formData.relationship.workPeriodStart.season > formData.relationship.workPeriodEnd.season ||
            formData.relationship.workPeriodStart.season != 4 && formData.relationship.workPeriodEnd.season == 4))  {
          newErrors['relationship.workPeriodEnd.season'] = '終了時期を正しく選択してください';
        }
      }
    }

    if (step === RESPONSE_FORM_STEP.QUESTIONS) {
      dynamicQuestions.forEach((question: QuestionItem) => {
        const fieldName = `question${question.order}`;
        if (question.order <= 10) {
          const fieldValue = formData.descriptiveQuestions[fieldName];
          const errorKey = `descriptiveQuestions.${fieldName}`;

          if (!fieldValue || fieldValue.trim() === '') {
            newErrors[errorKey] = '回答を入力してください';
          } else if (containsEmoji(fieldValue)) {
            newErrors[errorKey] = '絵文字は使用できません。';
          }
        }
      });
    }

    if (step === RESPONSE_FORM_STEP.SOCIAL_SKILL) {
      socialSkillQuestions.forEach((question: SocialSkill) => {
        const fieldName = `question${question.order}`;
        if (question.order <= 15) {
          const fieldValue = formData.socialSkills[fieldName];
          const errorKey = `socialSkills.${fieldName}`;
          if (fieldValue === null || fieldValue === undefined) {
            newErrors[errorKey] = '選択してください';
          }
        }
      });
    }

    if (step === RESPONSE_FORM_STEP.SURVEY) {
      surveyQuestions.forEach((question: Survey) => {
        const fieldName = `question${question.order}`;
        if (question.order <= 10) {
          const fieldValue = formData.questionnaire[fieldName];
          const errorKey = `questionnaire.${fieldName}`;
          if (question.input_type === 'textarea') {
            if (!fieldValue || fieldValue.trim() === '') {
              newErrors[errorKey] = 'この質問への回答は必須です';
            }
          } else {
            if (!fieldValue || (fieldValue !== '1' && fieldValue !== '0')) {
              newErrors[errorKey] = 'この質問への回答は必須です';
            }
          }
        }
      });
    }

    return newErrors;
  };

  const handleNext = (): void => {
    const newErrors = validateStep(currentStep);

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setCurrentStep(prev => prev + 1);
    window.scrollTo(0, 0);
  };

  const handlePrevious = (): void => {
    setCurrentStep(prev => prev - 1);
    window.scrollTo(0, 0);
  };

  const handleSubmit = async (): Promise<void> => {
    const newErrors = validateStep(currentStep);

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const browserInfo = getClientBrowserInfo();
      const osInfo = getClientOSInfo();
      const ipAddress = getClientIPAddress();

      const responseData = {
        respondent_id: respondentInfo?.id,
        candidate_id: candidateInfo?.id,

        res1_relationship_private: formData.relationship.private,
        res1_relationship_detail: formData.relationship.detail,
        res1_work_company: formData.relationship.workCompany,
        res1_work_details: formData.relationship.workDetails,
        res1_work_period_start_year: formData.relationship.workPeriodStart.year,
        res1_work_period_start_season: formData.relationship.workPeriodStart.season,
        res1_work_period_end_year: formData.relationship.currentlyWorking ? null : formData.relationship.workPeriodEnd.year,
        res1_work_period_end_season: formData.relationship.currentlyWorking ? null : formData.relationship.workPeriodEnd.season,
        res1_currently_working: formData.relationship.currentlyWorking,
        res1_activity_frequency: formData.relationship.activityFrequency,

        res2_q1: formData.descriptiveQuestions.question1,
        res2_q2: formData.descriptiveQuestions.question2,
        res2_q3: formData.descriptiveQuestions.question3,
        res2_q4: formData.descriptiveQuestions.question4,
        res2_q5: formData.descriptiveQuestions.question5,
        res2_q6: formData.descriptiveQuestions.question6,
        res2_q7: formData.descriptiveQuestions.question7,
        res2_q8: formData.descriptiveQuestions.question8,
        res2_q9: formData.descriptiveQuestions.question9,
        res2_q10: formData.descriptiveQuestions.question10,

        res3_social_skill: Object.values(formData.socialSkills),

        res4_q1: formData.questionnaire.question1,
        res4_q2: formData.questionnaire.question2,
        res4_q3: formData.questionnaire.question3,
        res4_q4: formData.questionnaire.question4,
        res4_q5: formData.questionnaire.question5,
        res4_q6: formData.questionnaire.question6,
        res4_q7: formData.questionnaire.question7,
        res4_q8: formData.questionnaire.question8,
        res4_q9: formData.questionnaire.question9,
        res4_q10: formData.questionnaire.question10,

        browser_info: browserInfo,
        device_info: osInfo,
        ip_address: ipAddress,
      };

      const response = await axios.post('/responses', responseData);

      if (response.status === 201 || response.status === 200) {
        setIsComplete(true);
      } else {
        setErrors({ submit: '回答情報の送信中にエラーが発生しました。もう一度お試しください。' });
      }
    } catch (error: unknown) {
      console.error('Error submitting form:', error);
      const axiosError = error as ApiError;
      setErrors({ submit: '回答情報の送信中にエラーが発生しました。もう一度お試しください。' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    // Add event listener
    window.addEventListener('resize', handleResize);
    // Cleanup on unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const progressBarPercent = (currentStep: number) => {
    if (width > 768) {
      return 'calc(5% + 20px + ' + (currentStep-1) + ' * calc(80% - 40px) / 4)';
    } else {
      return 'calc(20px + ' + (currentStep-1) + ' * calc(100% - 40px) / 4)';
    }
  }

  if (isComplete) {
    return (
      <div className="respondent-form-container">
        <Header user={null} />
        <Container>
          <div className="step-labels">
            <div className='step-title active'><span className="step-title-seq">1. </span>関係性</div>
            <div className='step-title active'><span className="step-title-seq">2. </span>記述式<br className="sp" />質問</div>
            <div className='step-title active'><span className="step-title-seq">3. </span>選択式<br className="sp" />質問</div>
            <div className='step-title active'><span className="step-title-seq">4. </span>アン<br className="sp" />ケート</div>
            <div className='step-title active'><span className="step-title-seq">5. </span>最終<br className="sp" />確認</div>
          </div>
          <div className="step-progress">
            <div 
              className="step-progress-line" 
              style={{ width: `${progressBarPercent(6)}` }}
            ></div>
            <div className="step-item">
              <div className='step-number active'></div>
            </div>
            <div className="step-item">
              <div className='step-number active'></div>
            </div>
            <div className="step-item">
              <div className='step-number active'></div>
            </div>
            <div className="step-item">
              <div className='step-number active'></div>
            </div>
            <div className="step-item">
              <div className='step-number active'></div>
            </div>
          </div>
        </Container>
        <Container className="blank-content" style={{ minHeight: 'calc(100vh - 400px)'}}>
          <div className="blank-form-container">
            <h4 className="mb-4">ご協力ありがとうございました。</h4>

            <div className="text-center">
                <p>
                  リファレンスチェックはこれにて完了となります。<br />
                  ご回答いただいた内容は、候補者の方に関する参考情報として、慎重かつ適切に活用させていただきます。
                </p>
                <p>
                  お忙しいところ貴重なお時間を割いてご対応いただきましたこと、心より感謝申し上げます。<br />
                  ご不明な点などございましたら、いつでもご遠慮なくご連絡ください。
                </p>
              </div>
          </div>
        </Container>
      </div>
    );
  }

  if (isConflict) {
    return (
      <div className="respondent-form-container">
        <Header user={null} />
        <Container className="blank-content" style={{ minHeight: 'calc(100vh - 300px)'}}>
          <div className="blank-form-container">
            <h4 className="mb-4">ご回答いただき、誠にありがとうございました。</h4>

            <div className="text-center">
                <p>
                  こちらのリファレンスチェックはすでに完了しております。
                </p>
              </div>
          </div>
        </Container>
      </div>
    );
  }

  if (loading || !candidateInfo) {
    return (
      <div className="respondent-form-container">
        <Header user={null} />
        <Container className="blank-content" style={{ minHeight: 'calc(100vh - 300px)'}}>
          <div className="blank-form-container">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">情報を読み込んでいます...</p>
          </div>
        </Container>
      </div>
    );
  }

  if (errors.submit) {
    return (
      <div className="respondent-form-container">
        <Header user={null} />
        <Container className="blank-content" style={{ minHeight: 'calc(100vh - 300px)'}}>
          <div className="blank-form-container">
            <h4 className="mb-4">エラー発生</h4>

            <div className="text-center">
              <p>{errors.submit}</p>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="respondent-form-container">
      <Header user={null} />

      <Container>
        <div className="step-labels">
          <div className={`step-title ${currentStep >= RESPONSE_FORM_STEP.RELATIONSHIP ? 'active' : ''}`}><span className="step-title-seq">1. </span>関係性</div>
          <div className={`step-title ${currentStep >= RESPONSE_FORM_STEP.QUESTIONS ? 'active' : ''}`}><span className="step-title-seq">2. </span>記述式<br className="sp" />質問</div>
          <div className={`step-title ${currentStep >= RESPONSE_FORM_STEP.SOCIAL_SKILL ? 'active' : ''}`}><span className="step-title-seq">3. </span>選択式<br className="sp" />質問</div>
          <div className={`step-title ${currentStep >= RESPONSE_FORM_STEP.SURVEY ? 'active' : ''}`}><span className="step-title-seq">4. </span>アン<br className="sp" />ケート</div>
          <div className={`step-title ${currentStep >= RESPONSE_FORM_STEP.CONFIRM ? 'active' : ''}`}><span className="step-title-seq">5. </span>最終<br className="sp" />確認</div>
        </div>
        <div className="step-progress">
          <div 
            className="step-progress-line" 
            style={{ width: `${progressBarPercent(currentStep)}` }}
          ></div>
          <div className="step-item">
            <div className={`step-number ${currentStep >= RESPONSE_FORM_STEP.RELATIONSHIP ? 'active' : ''}`}></div>
          </div>
          <div className="step-item">
            <div className={`step-number ${currentStep >= RESPONSE_FORM_STEP.QUESTIONS ? 'active' : ''}`}></div>
          </div>
          <div className="step-item">
            <div className={`step-number ${currentStep >= RESPONSE_FORM_STEP.SOCIAL_SKILL ? 'active' : ''}`}></div>
          </div>
          <div className="step-item">
            <div className={`step-number ${currentStep >= RESPONSE_FORM_STEP.SURVEY ? 'active' : ''}`}></div>
          </div>
          <div className="step-item">
            <div className={`step-number ${currentStep >= RESPONSE_FORM_STEP.CONFIRM ? 'active' : ''}`}></div>
          </div>
        </div>

        <div className="form-section">
          {currentStep === RESPONSE_FORM_STEP.RELATIONSHIP && (
            <div>
              <div className="form-section-header">
                {candidateInfo.name}さんとの関係性について教えてください <span className="required-mark">*</span>
              </div>
              <div className="form-section-body">
                <Form.Group className="form-group">
                  <Form.Label className="form-label">Q1. {candidateInfo.name}さんはどちらかというと <span className="required-mark">*</span></Form.Label>
                  <div>
                    <Form.Check
                      type="radio"
                      id="relationshipPrivate-work"
                      name="relationship.private"
                      value="work"
                      label="仕事やプライベートを一緒にした時間の方が長い"
                      checked={formData.relationship.private === 'work'}
                      onChange={handleChange}
                      isInvalid={!!errors['relationship.private']}
                      className="form-check"
                    />
                    <Form.Check
                      type="radio"
                      id="relationshipPrivate-private"
                      name="relationship.private"
                      value="private"
                      label="プライベートで一緒に過ごした時間の方が長い"
                      checked={formData.relationship.private === 'private'}
                      onChange={handleChange}
                      isInvalid={!!errors['relationship.private']}
                      className="form-check"
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors['relationship.private']}
                    </Form.Control.Feedback>
                  </div>
                </Form.Group>

                <Form.Group className="form-group">
                  <Form.Label className="form-label">Q2. {candidateInfo.name}さんと一緒に活動していた当時の企業名・団体名などを教えてください <span className="required-mark">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="relationship.workCompany"
                    value={formData.relationship.workCompany}
                    onChange={handleChange}
                    isInvalid={!!errors['relationship.workCompany']}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors['relationship.workCompany']}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="form-group">
                  <Form.Label className="form-label">Q3. {candidateInfo.name}さんとの関係性で最も近いものを選んでください <span className="required-mark">*</span></Form.Label>
                  <Form.Select
                    name="relationship.detail"
                    value={formData.relationship.detail}
                    onChange={handleChange}
                    isInvalid={!!errors['relationship.detail']}
                    className="form-select"
                  >
                    <option value="">選択してください</option>
                    {RELATIONSHIP_OPTIONS.map((option) => (
                      <option key={option.code} value={option.code}>
                        {option.text}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors['relationship.detail']}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="form-group">
                  <Form.Label className="form-label">Q4. {candidateInfo.name}さんと一緒にした仕事やプロジェクトの内容を具体的に教えてください <span className="required-mark">*</span></Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="relationship.workDetails"
                    value={formData.relationship.workDetails}
                    onChange={handleChange}
                    isInvalid={!!errors['relationship.workDetails']}
                    className="form-textarea"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors['relationship.workDetails']}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="form-group">
                  <Form.Label className="form-label">Q5. {candidateInfo.name}さんと活動していた期間について教えてください <span className="required-mark">*</span></Form.Label>
                  <Row>
                    <Col md={2}>
                      <Form.Label className='mb-0'><small>開始年</small></Form.Label>
                      <Form.Select
                        name="relationship.workPeriodStart.year"
                        value={formData.relationship.workPeriodStart.year}
                        onChange={handleChange}
                        isInvalid={!!errors['relationship.workPeriodStart.year']}
                        className="form-select"
                      >
                        <option value="">選択</option>
                        {Array.from({ length: 20 }, (_, i) => new Date().getFullYear() - i).map(year => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors['relationship.workPeriodStart.year']}
                      </Form.Control.Feedback>
                    </Col>
                    <Col md={2}>
                      <Form.Label className='mb-0'><small>時期</small></Form.Label>
                      <Form.Select
                        name="relationship.workPeriodStart.season"
                        value={formData.relationship.workPeriodStart.season}
                        onChange={handleChange}
                        isInvalid={!!errors['relationship.workPeriodStart.season']}
                        className="form-select"
                      >
                        <option value="">選択</option>
                        {PERIOD_OPTIONS.map((option) => (
                          <option key={option.code} value={option.code}>
                            {option.text}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors['relationship.workPeriodStart.season']}
                      </Form.Control.Feedback>
                    </Col>
                    <Col md={1} className="text-center align-self-end">
                      <span>～</span>
                    </Col>
                    <Col md={2}>
                      <Form.Label className='mb-0'><small>終了年</small></Form.Label>
                      <Form.Select
                        name="relationship.workPeriodEnd.year"
                        value={formData.relationship.workPeriodEnd.year}
                        onChange={handleChange}
                        disabled={formData.relationship.currentlyWorking}
                        isInvalid={!formData.relationship.currentlyWorking && !!errors['relationship.workPeriodEnd.year']}
                        className="form-select"
                      >
                        <option value="">選択</option>
                        {Array.from({ length: 20 }, (_, i) => new Date().getFullYear() - i).map(year => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors['relationship.workPeriodEnd.year']}
                      </Form.Control.Feedback>
                    </Col>
                    <Col md={2}>
                      <Form.Label className='mb-0'><small>時期</small></Form.Label>
                      <Form.Select
                        name="relationship.workPeriodEnd.season"
                        value={formData.relationship.workPeriodEnd.season}
                        onChange={handleChange}
                        disabled={formData.relationship.currentlyWorking}
                        isInvalid={!formData.relationship.currentlyWorking && !!errors['relationship.workPeriodEnd.season']}
                        className="form-select"
                      >
                        <option value="">選択</option>
                        {PERIOD_OPTIONS.map((option) => (
                          <option key={option.code} value={option.code}>
                            {option.text}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors['relationship.workPeriodEnd.season']}
                      </Form.Control.Feedback>
                    </Col>
                  </Row>
                  <Form.Check
                    type="checkbox"
                    id="currentlyWorking"
                    name="relationship.currentlyWorking"
                    label="現在も働いている"
                    checked={formData.relationship.currentlyWorking}
                    onChange={handleCheckboxChange}
                    className="mt-2 form-check"
                  />
                </Form.Group>

                <Form.Group className="form-group">
                  <Form.Label className="form-label">Q6. {candidateInfo.name}さんとの活動頻度を教えてください <span className="required-mark">*</span></Form.Label>
                  <Form.Select
                    name="relationship.activityFrequency"
                    value={formData.relationship.activityFrequency}
                    onChange={handleChange}
                    isInvalid={!!errors['relationship.activityFrequency']}
                    className="form-select"
                  >
                    <option value="">選択してください</option>
                    {ACTIVITY_FREQUENCY_OPTIONS.map((option) => (
                      <option key={option.code} value={option.code}>
                        {option.text}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors['relationship.activityFrequency']}
                  </Form.Control.Feedback>
                </Form.Group>
                <div>
                  <Button 
                    variant='success'
                    className="btn-next" 
                    onClick={handleNext}
                    disabled={isSubmitting}
                  >
                    次へ
                  </Button>
                </div>
              </div>
            </div>
          )}

          {currentStep === RESPONSE_FORM_STEP.QUESTIONS && (
            <div>
              <div className="form-section-header">
                {candidateInfo.name}さんについて教えてください <span className="required-mark">*</span>
              </div>
              <div className="form-section-body">
                <div className='question-no-replay'>
                  【必須回答】<br/>
                  {candidateInfo.name}さんに回答は伝わりません
                </div>
                <Form.Label className="form-label">全{dynamicQuestions.length}問 <span className="question-note">（この回答は{candidateInfo.name}さんに伝わりません）</span></Form.Label>
                <div className="questionnaire-section">
                  {dynamicQuestions.length > 0 ? (
                    dynamicQuestions.map((question: QuestionItem) => {
                      if (question.order > 10) return null;
                      const fieldName = `question${question.order}`;
                      const questionText = question.text.replace(/{candidate_name}|候補者/g, candidateInfo.name);
                      const errorKey = `descriptiveQuestions.${fieldName}`;

                      return (
                        <Form.Group key={`descriptive-${question.order}`} className="form-group">
                          <Form.Label className="form-label">
                            Q{question.order}. {questionText} <span className="required-mark">*</span>
                          </Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={4}
                            name={`descriptiveQuestions.${fieldName}`}
                            value={formData.descriptiveQuestions[fieldName] || ''}
                            onChange={handleChange}
                            isInvalid={!!(errors as any)[errorKey]}
                            placeholder="具体的なエピソードを交えて教えてください"
                            className="form-textarea"
                          />
                          {errors[errorKey] && (
                            <Form.Control.Feedback type="invalid" className="d-block">
                              {errors[errorKey]}
                            </Form.Control.Feedback>
                          )}
                        </Form.Group>
                      );
                    })
                  ) : (
                    <div className="text-center">質問が設定されていません。</div>
                  )}
                </div>
                <div>
                  <Button 
                    variant='outline-secondary'
                    className="btn-previous" 
                    onClick={handlePrevious}
                    disabled={isSubmitting}
                  >
                    戻る
                  </Button>
                  <Button 
                    variant='success'
                    className="btn-next" 
                    onClick={handleNext}
                    disabled={isSubmitting}
                  >
                    次へ
                  </Button>
                </div>
              </div>
            </div>
          )}

          {currentStep === RESPONSE_FORM_STEP.SOCIAL_SKILL && (
            <div>
              <div className="form-section-header">
                {candidateInfo.name}さんについて教えてください <span className="required-mark">*</span>
              </div>
              <div className="form-section-body">
                <div className='question-no-replay'>
                  【必須回答】<br/>
                  {candidateInfo.name}さんに回答は伝わりません
                </div>
                <Form.Label className="form-label">全{socialSkillQuestions.length}問 <span className="question-note">（この回答は{candidateInfo.name}さんに伝わりません）</span></Form.Label>
                <div className="questionnaire-section">
                  {socialSkillQuestions.length > 0 ? (
                    socialSkillQuestions.map((question: SocialSkill) => {
                      if (question.order > 15) return null;
                      const fieldName = `question${question.order}`;
                      const errorKey = `socialSkills.${fieldName}`;

                      return (
                        <Form.Group key={`social-skill-${question.order}`} className="form-group questionnaire-item">
                          <Form.Label className="form-label">
                            Q{question.order}. {question.text} <span className="required-mark">*</span>
                          </Form.Label>
                          <div className="radio-group">
                            <Form.Check
                              type="radio"
                              id={`${fieldName}-4`}
                              name={`socialSkills.${fieldName}`}
                              label="非常にそう思う"
                              value="4"
                              checked={formData.socialSkills[fieldName] === 4}
                              onChange={handleChange}
                              isInvalid={!!errors[errorKey]}
                              className="radio-option"
                            />
                            <Form.Check
                              type="radio"
                              id={`${fieldName}-3`}
                              name={`socialSkills.${fieldName}`}
                              label="そう思う"
                              value="3"
                              checked={formData.socialSkills[fieldName] === 3}
                              onChange={handleChange}
                              isInvalid={!!errors[errorKey]}
                              className="radio-option"
                            />
                            <Form.Check
                              type="radio"
                              id={`${fieldName}-2`}
                              name={`socialSkills.${fieldName}`}
                              label="そう思わない"
                              value="2"
                              checked={formData.socialSkills[fieldName] === 2}
                              onChange={handleChange}
                              isInvalid={!!errors[errorKey]}
                              className="radio-option"
                            />
                            <Form.Check
                              type="radio"
                              id={`${fieldName}-1`}
                              name={`socialSkills.${fieldName}`}
                              label="全くそう思わない"
                              value="1"
                              checked={formData.socialSkills[fieldName] === 1}
                              onChange={handleChange}
                              isInvalid={!!errors[errorKey]}
                              className="radio-option"
                            />
                          </div>
                          {errors[errorKey] && (
                            <Form.Control.Feedback type="invalid" className="d-block">
                              {errors[errorKey]}
                            </Form.Control.Feedback>
                          )}
                        </Form.Group>
                      );
                    })
                  ) : (
                    <div className="text-center">質問が設定されていません。</div>
                  )}
                </div>
                <div>
                  <Button 
                    variant='outline-secondary'
                    className="btn-previous" 
                    onClick={handlePrevious}
                    disabled={isSubmitting}
                  >
                    戻る
                  </Button>
                  <Button 
                    variant='success'
                    className="btn-next" 
                    onClick={handleNext}
                    disabled={isSubmitting}
                  >
                    次へ
                  </Button>
                </div>
              </div>
            </div>
          )}

          {currentStep === RESPONSE_FORM_STEP.SURVEY && (
            <div>
              <div className="form-section-header">
                アンケートにご協力ください <span className="required-mark">*</span>
              </div>
              <div className="form-section-body">
                <Form.Label className="form-label">全{surveyQuestions.length}問</Form.Label>
                <div className="questionnaire-section">
                  {surveyQuestions.length > 0 ? (
                    surveyQuestions.map((question: Survey) => {
                      if (question.order > 10) return null;
                      const fieldName = `question${question.order}`;
                      const errorKey = `questionnaire.${fieldName}`;

                      return (
                        <Form.Group key={`survey-${question.order}`} className="form-group questionnaire-item">
                          <Form.Label className="form-label">
                            Q{question.order}. {question.text} <span className="required-mark">*</span>
                          </Form.Label>
                          
                          {question.input_type === 'textarea' ? (
                            <Form.Control
                              as="textarea"
                              rows={4}
                              name={`questionnaire.${fieldName}`}
                              value={formData.questionnaire[fieldName] || ''}
                              onChange={handleChange}
                              isInvalid={!!errors[errorKey]}
                              className="form-textarea"
                            />
                          ) : (
                            <div className="radio-group d-flex">
                              <Form.Check
                                type="radio"
                                id={`${fieldName}-1`}
                                name={`questionnaire.${fieldName}`}
                                label="はい"
                                value="1"
                                checked={formData.questionnaire[fieldName] === '1'}
                                onChange={handleChange}
                                isInvalid={!!errors[errorKey]}
                                className="radio-option"
                              />
                              <Form.Check
                                type="radio"
                                id={`${fieldName}-0`}
                                name={`questionnaire.${fieldName}`}
                                label="いいえ"
                                value="0"
                                checked={formData.questionnaire[fieldName] === '0'}
                                onChange={handleChange}
                                isInvalid={!!errors[errorKey]}
                                className="radio-option"
                              />
                            </div>
                          )}
                          
                          {errors[errorKey] && (
                            <Form.Control.Feedback type="invalid" className="d-block">
                              {errors[errorKey]}
                            </Form.Control.Feedback>
                          )}
                        </Form.Group>
                      );
                    })
                  ) : (
                    <div className="text-center">アンケートが設定されていません。</div>
                  )}
                </div>
                <div>
                  <Button 
                    variant='outline-secondary'
                    className="btn-previous" 
                    onClick={handlePrevious}
                    disabled={isSubmitting}
                  >
                    戻る
                  </Button>
                  <Button 
                    variant='success'
                    className="btn-next" 
                    onClick={handleNext}
                    disabled={isSubmitting}
                  >
                    次へ
                  </Button>
                </div>
              </div>
            </div>
          )}

          {currentStep === RESPONSE_FORM_STEP.CONFIRM && (
            <div>
              <div className="form-section-header">
                入力内容の確認
              </div>
              <div className="form-section-body">
                <div className="confirmation-section relationship-section">
                  <div className='d-flex mb-3'>
                    <Image src="/confirm-mark.svg" />
                    <div className="confirmation-title">{candidateInfo.name}さんとの関係性</div>
                  </div>
                  <div className="confirmation-item">
                    <div className="confirmation-label">所属：</div>
                    <div className="confirmation-value">{formData.relationship.workCompany}</div>
                  </div>
                  <div className="confirmation-item">
                    <div className="confirmation-label">関係性：</div>
                    <div className="confirmation-value">
                      {candidateInfo.name}さんの{getRelationshipString(Number(formData.relationship.detail))}<br/>
                      （{formData.relationship.private === 'work' ? '仕事やプライベートを一緒にした時間の方が長い' : 'プライベートで一緒に過ごした時間の方が長い'}）
                    </div>
                  </div>
                  <div className="confirmation-item">
                    <div className="confirmation-label">活動内容：</div>
                    <div className="confirmation-value">{formData.relationship.workDetails}</div>
                  </div>
                  <div className="confirmation-item">
                    <div className="confirmation-label">期間：</div>
                    <div className="confirmation-value">
                      {formData.relationship.workPeriodStart.year}年 {getPeriodString(Number(formData.relationship.workPeriodStart.season))} ～ 
                      {formData.relationship.currentlyWorking ? '（現在も一緒に活動している）' : `${formData.relationship.workPeriodEnd.year}年 ${getPeriodString(Number(formData.relationship.workPeriodEnd.season))}`}
                    </div>
                  </div>
                  <div className="confirmation-item">
                    <div className="confirmation-label">頻度：</div>
                    <div className="confirmation-value">{getActivityFrequencyString(Number(formData.relationship.activityFrequency))}</div>
                  </div>
                </div>

                <div className="confirmation-section questions-confirm">
                  <div className='d-flex mb-3'>
                    <Image src="/confirm-mark.svg" />
                    <div className="confirmation-title">記述式質問に対する回答</div>
                  </div>
                  <Form.Label className="form-label">全{dynamicQuestions.length}問 <span className="question-note">（この回答は{candidateInfo.name}さんに伝わりません）</span></Form.Label>
                  {dynamicQuestions.length > 0 ? (
                    dynamicQuestions.map((question: QuestionItem) => {
                      if (question.order > 10) return null;
                      const questionText = question.text.replace(/{candidate_name}|候補者/g, candidateInfo.name);
                      return (
                        <div key={question.order} className="confirmation-item">
                          <div className="confirmation-label">Q{question.order}. {questionText}</div>
                          <div className="confirmation-value">{formData.descriptiveQuestions[`question${question.order}`]}</div>
                        </div>
                      );
                    })
                  ) : (
                    <div className='questionnaire-confirm'>
                      <div className="text-center">質問が設定されていません。</div>
                    </div>
                  )}
                </div>

                <div className="confirmation-section social-skills-confirm">
                  <div className='d-flex mb-3'>
                    <Image src="/confirm-mark.svg" />
                    <div className="confirmation-title">選択式質問に対する回答</div>
                  </div>
                  <Form.Label className="form-label">全{socialSkillQuestions.length}問 <span className="question-note">（この回答は{candidateInfo.name}さんに伝わりません）</span></Form.Label>
                  <div className='questionnaire-confirm'>
                    {socialSkillQuestions.length > 0 ? (
                      socialSkillQuestions.map((question: SocialSkill) => {
                        if (question.order > 15) return null;
                        const fieldValue = formData.socialSkills[`question${question.order}`];
                        const getResponseText = (value: number | null) => {
                          switch (value) {
                            case 4: return '非常にそう思う';
                            case 3: return 'そう思う';
                            case 2: return 'そう思わない';
                            case 1: return '全くそう思わない';
                            default: return '未回答';
                          }
                        };
                        return (
                          <div key={question.order} className="confirmation-item">
                            <div className="confirmation-label">Q{question.order}. {question.text}</div>
                            <div className="confirmation-value">{getResponseText(fieldValue)}</div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-center">質問が設定されていません。</div>
                    )}
                  </div>
                </div>

                <div className="confirmation-section mb-2">
                  <div className='d-flex mb-3'>
                    <Image src="/confirm-mark.svg" />
                    <div className="confirmation-title">アンケートの回答</div>
                  </div>
                  <Form.Label className="form-label">全{surveyQuestions.length}問</Form.Label>
                  <div className='questionnaire-confirm'>
                    {surveyQuestions.length > 0 ? (
                      surveyQuestions.map((question: Survey) => {
                        if (question.order > 10) return null;
                        const fieldValue = formData.questionnaire[`question${question.order}`];
                        const displayValue = formatSurveyResponse(fieldValue, question.input_type);
                        
                        return (
                          <div key={question.order} className="confirmation-item">
                            <div className="confirmation-label">Q{question.order}. {question.text}</div>
                            <div className="confirmation-value">{displayValue}</div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-center">アンケートが設定されていません。</div>
                    )}
                  </div>
                </div>

                <div className='mb-5'>
                  <p>
                    記載内容に問題がなければ、入力を完了してください。<br/>
                    推薦の記述は以上です。確認画面へ進んでください。
                  </p>
                </div>
                <div>
                  <Button 
                    variant='outline-secondary'
                    className="btn-previous" 
                    onClick={handlePrevious}
                    disabled={isSubmitting}
                  >
                    戻る
                  </Button>
                  <Button 
                    variant='success'
                    className="btn-submit" 
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? '送信中...' : '記載完了'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </Container>
    </div>
  );
};

export default RespondenseForm;
